import type { DeferredBindingState } from "../api/deferred-originations";

/** Visual tone for origination status in list + receipt (aligned with send). */
export type OriginationStatusTone =
  | "initial"
  | "progress"
  | "completed"
  | "failed"
  | "expired";

/** Hex colors for status tones (list text+icon, receipt text). */
export const STATUS_TONE_COLOR: Record<OriginationStatusTone, string> = {
  initial: "#F28D19",
  progress: "#252525",
  completed: "#31C859",
  failed: "#FF3B30",
  expired: "#8a8a8a",
};

const normalizeOp = (status?: string | null): string =>
  (status ?? "").toLowerCase();

/** i18n key under `receipt.*` for a deferred payout binding state. */
export const bindingStatusKey = (state: DeferredBindingState): string => {
  switch (state) {
    case "payment_bound":
      return "receipt.statusPaymentBound";
    case "awaiting_claim":
      return "receipt.statusAwaitingClaim";
    case "payout_bound":
      return "receipt.statusPayoutBound";
    case "executable":
      return "receipt.statusExecutable";
    case "ofac_rejected":
      return "receipt.statusRejected";
    case "expired":
      return "receipt.statusExpired";
    case "cancelled":
      return "receipt.statusCancelled";
    default:
      return "receipt.status";
  }
};

/**
 * Unified label for sender UI: operation status wins when present and
 * meaningful; otherwise fall back to binding state.
 */
export const originationStatusKey = (
  bindingState: DeferredBindingState,
  operationStatus?: string | null
): string => {
  const op = normalizeOp(operationStatus);

  if (op === "completed" || op === "committed" || op === "funded") {
    return "receipt.statusCompleted";
  }
  if (op === "failed") {
    return "receipt.statusFailed";
  }
  if (op === "cancelled" || op === "canceled") {
    return "receipt.statusCancelled";
  }
  if (op === "processing") {
    return "receipt.statusProcessing";
  }
  if (op === "pending" || op === "created") {
    // Bound operation still warming up — prefer processing over "awaiting claim".
    if (bindingState === "executable" || bindingState === "payout_bound") {
      return "receipt.statusProcessing";
    }
    return "receipt.statusPending";
  }

  return bindingStatusKey(bindingState);
};

export const originationStatusTone = (
  bindingState: DeferredBindingState,
  operationStatus?: string | null
): OriginationStatusTone => {
  const op = normalizeOp(operationStatus);

  if (op === "completed" || op === "committed" || op === "funded") {
    return "completed";
  }
  if (op === "failed") {
    return "failed";
  }
  if (op === "cancelled" || op === "canceled" || bindingState === "cancelled") {
    return "failed";
  }
  if (bindingState === "ofac_rejected") {
    return "failed";
  }
  if (bindingState === "expired") {
    return "expired";
  }
  if (
    op === "processing" ||
    bindingState === "executable" ||
    bindingState === "payout_bound"
  ) {
    return "progress";
  }
  // payment_bound, awaiting_claim, pending/created → initial (orange)
  return "initial";
};

/** Whether the origination is settled from the sender's perspective. */
export const isOriginationComplete = (
  _state: DeferredBindingState,
  operationStatus?: string | null
): boolean => {
  const op = normalizeOp(operationStatus);
  return op === "completed" || op === "committed" || op === "funded";
};

export const isOriginationFailed = (
  operationStatus?: string | null
): boolean => normalizeOp(operationStatus) === "failed";

export const isOriginationCancelled = (
  operationStatus?: string | null
): boolean => {
  const op = normalizeOp(operationStatus);
  return op === "cancelled" || op === "canceled";
};
